import React, { useEffect, useMemo, useState } from "react";

import { Box, Divider, IconButton, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/CancelSharp";

import { KITS } from "helpers/apis";
import useAxios from "helpers/axios";
import { colors } from "theme";
import { PlatformType } from "types/design";

import { SequencingKits } from "./SequencingKits";
import { SequencingKit } from "./type";

interface SequencingEstimatorProps {
  reads: number;
  onClose: () => void;
  showAll: boolean;
}

export const SequencingEstimator: React.FC<SequencingEstimatorProps> = ({
  reads,
  onClose,
  showAll,
}) => {
  const [kits, setKits] = useState<SequencingKit[]>([]);
  const illuminaKits = useMemo(
    () =>
      kits.filter(
        (kit) =>
          kit.platformType === PlatformType.ILLUMINA && kit.status === "active"
      ),
    [kits]
  );
  const ionTorrentKits = useMemo(
    () =>
      kits.filter(
        (kit) =>
          kit.platformType === PlatformType.IONTORRENT &&
          kit.status === "active"
      ),
    [kits]
  );

  const { fetchData } = useAxios({
    config: {
      url: KITS,
    },
    callOnInit: false,
    successCallback: ({ response }: any) => {
      if (Array.isArray(response?.data)) {
        setKits(response.data);
      } else {
        setKits([]);
      }
    },
  });
  const classes = useStyles();

  useEffect(() => {
    if (reads) {
      fetchData({
        params: {
          reads,
          ...(showAll ? {} : { platformType: PlatformType.ILLUMINA }),
        },
      });
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [showAll, reads]);

  return (
    <Box
      id="sequencing-estimator-content"
      borderTop={`1px solid ${colors.grey[400]}`}
      borderRadius="0 0 4px 4px"
      bgcolor={colors.white}
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      width={1}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        width={1}
        padding={2}
        boxSizing="border-box"
      >
        <Typography variant="h4">Sequencing Estimator</Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={() => onClose()}
          size="large">
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </Box>
      <SequencingKits
        kits={illuminaKits}
        reads={reads}
        platformType={PlatformType.ILLUMINA}
      />
      {showAll && (
        <>
          <Divider className={classes.divider} />
          <SequencingKits
            kits={ionTorrentKits}
            reads={reads}
            platformType={PlatformType.IONTORRENT}
          />
        </>
      )}
    </Box>
  );
};

const useStyles = makeStyles({
  closeButton: {
    padding: 0,
  },
  closeIcon: {
    color: colors.grey[300],
  },
  divider: {
    width: "calc(100% - 32px)",
    margin: "0 16px",
  },
});
